import { useQueryClient } from "@tanstack/react-query";
import { LoginModal } from "hub/src//components/common/modal/LoginModal";
import LoadingPage from "hub/src/components/common/LoadingPage";
import { FirebaseAnalyticsProvider } from "hub/src/context/FirebaseAnalyticsProvider";
import { NotificationsProvider } from "hub/src/context/NotificationsProvider";
import { useQueryCacheRestoring } from "hub/src/context/PlatformSpecificQueryClientProvider/useQueryCacheRestoring";
import { useIFrameCheckSession } from "hub/src/hooks/iframe/useIFrameCheckSession";
import { useIFramePageSize } from "hub/src/hooks/iframe/useIFramePageSize";
import { getDeviceInfo } from "hub/src/utils/getDeviceInfo";
import { getSocietyId } from "hub/src/utils/getSocietyId";
import { usePhoneMediaQuery } from "hub/src/utils/media-queries";
import { getDefaultAppRoute } from "hub/src/utils/routes/getDefaultAppRoute";
import lodashIsNill from "lodash/fp/isNil";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { apiRequest } from "shared/api/apiRequest";
import {
  getSocietyAuthContract,
  postSocietyAuthContract,
} from "shared/api/contracts/society/societyId/auth";
import { postSocietyProfileLogoutContract } from "shared/api/contracts/society/societyId/profiles/profileId/logout";
import { getSocietyTokenValueContract } from "shared/api/contracts/society/societyId/token/tokenValue";
import type { RouteParams } from "shared/api/types/society/[societyId]/app-route-config";
import type { SocietyAuthBodyInput } from "shared/api/types/society/[societyId]/auth";
import type { SpecialResponse } from "shared/api/types/society/[societyId]/token/[tokenValue]";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { ThemeProvider } from "shared/components/ThemeProvider";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { CustomSessionContext } from "shared/context/CustomSessionContext";
import { ProfileContext } from "shared/context/ProfileContext";
import { SocietySessionContext } from "shared/context/SocietySessionContext";
import { VisibleViewPortProvider } from "shared/context/VisibleViewPortContext";
import { FALLBACK_DEFAULT_APP_ROUTE } from "shared/helpers/default-app-route/defaultAppRouteSetting";
import type { ProfileView } from "shared/mappers/database/profile/profile";
import type { SessionView } from "shared/mappers/database/session/session";
import type { AsyncFunction } from "shared/types/AsyncFunction";

const isNetworkError = (error: Error): boolean => {
  return error.name === "TypeError" && error.message.includes("fetch");
};

/**
 * Owns the iframe viewport state in its OWN component, deliberately separate
 * from UserHubCustomSessionProvider. `useIFramePageSize` updates on every
 * scroll/resize frame; if that state lived in the session provider it would
 * re-render it (and recompute its context value) on every frame, cascading to
 * every `useCustomSession()` consumer — forms, the survey, etc. Isolating it
 * here means only VisibleViewPortContext consumers (modals) re-render, while
 * `children` (a stable element) is passed straight through untouched.
 */
const HubVisibleViewPortProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const visibleViewPort = useIFramePageSize();
  return (
    <VisibleViewPortProvider value={visibleViewPort}>
      {children}
    </VisibleViewPortProvider>
  );
};

export const UserHubCustomSessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const isPhoneMedia = usePhoneMediaQuery();

  const { whenCacheRestored } = useQueryCacheRestoring();

  // State
  const [session, _setSession] = useState<SessionView | null>(null);
  const [sessionLoading, setSessionLoading] = useState<boolean>(false);

  const setSession: Dispatch<SetStateAction<SessionView | null>> = useCallback(
    (action) => {
      const nextValue =
        typeof action === "function"
          ? action(queryClient.getQueryData(["session"]) || session || null)
          : action;
      queryClient.setQueryData(["session"], nextValue);
      _setSession(nextValue);
    },
    [queryClient, session, _setSession],
  );

  const requestSession = async () => {
    try {
      await whenCacheRestored();
      const response = await apiRequest({
        contract: getSocietyAuthContract,
        inputs: {
          params: {
            societyId: getSocietyId(),
          },
        },
      });
      return response;
    } catch (_error) {
      const error = _error as Error;
      const session =
        queryClient.getQueryData<SessionView>(["session"]) || null;
      if (isNetworkError(error) && !!session) {
        return session;
      } else {
        throw error;
      }
    }
  };

  // Router
  const router = useCustomRouter();

  const loginModalControl = usePillarModalControl<null>();

  // Refs to remember which tokens we've already handled
  const lastTokenRef = useRef<string | null>(null);
  const lastImpersonateRef = useRef<string | null>(null);

  const clearAppCache = () => {
    if (!getDeviceInfo().isNativeApp) {
      return;
    }

    queryClient.removeQueries();
  };

  // On mount & whenever router.state.token or .impersonate changes:
  useEffect(() => {
    const { token, impersonate } = router.state || {};

    // If there's a new token that we haven't processed yet
    if (
      token &&
      token !== lastTokenRef.current &&
      (typeof token === "string" || typeof token === "number")
    ) {
      lastTokenRef.current = token as string;
      setSessionLoading(true);
      clearAppCache();
      tokenCallback(token as string);
      router.removeFromState("token");
      return;
    }

    // If there's a new impersonate token we haven't processed yet
    if (
      impersonate &&
      impersonate !== lastImpersonateRef.current &&
      typeof impersonate === "string"
    ) {
      lastImpersonateRef.current = impersonate as string;
      setSessionLoading(true);
      clearAppCache();
      tokenCallback(impersonate as string);
      router.removeFromState("impersonate");
      return;
    }

    // If nothing special, just try to refresh session once
    if (!session && !sessionLoading) {
      refreshSessionCallback();
    }
  }, [router.state?.token, router.state?.impersonate]);

  // Logout flow
  const logoutCallback = async () => {
    setSessionLoading(true);
    await apiRequest({
      contract: postSocietyProfileLogoutContract,
      inputs: {
        params: {
          societyId: getSocietyId(),
          profileId: session!.profileId!,
        },
      },
    });
    clearAppCache();
    router.push("/login");
    setSession(null);
    await refreshSessionCallback();
  };

  // Refresh session & handle redirects
  const refreshSessionCallback = async (
    redirectUrlAfterLogin?: string,
    routeParams?: RouteParams,
  ) => {
    try {
      setSessionLoading(true);
      const response = await requestSession();
      setSession(response);
      await redirectUrlAfterLoginCallback(
        response,
        redirectUrlAfterLogin ?? null,
        routeParams,
      );
    } catch (error) {
      console.error(error);
      router.push("/login", {
        ...(routeParams ?? {}),
        redirectUrlAfterLogin: router.pathname,
      });
    }
  };

  // After login, decide where to go
  const redirectUrlAfterLoginCallback = async (
    sessionData: SessionView | null,
    redirectUrlAfterLogin?: string | null,
    routeParams?: RouteParams,
  ) => {
    if (sessionData?.societyUserId) {
      const tokenUsageFile = router.state?.t;
      if (tokenUsageFile) {
        const response = await apiRequest({
          contract: getSocietyTokenValueContract,
          inputs: {
            params: {
              societyId: sessionData.societyId!,
              tokenValue: tokenUsageFile.toString(),
            },
          },
        });
        if ("usageFile" in response) {
          router.push(
            `/shared-urls/${response.usageFile?.fileId}`,
            routeParams,
          );
          return;
        }
      }
      if (redirectUrlAfterLogin) {
        // If the requested redirect URL is part of an OIDC flow, redirect using window.location.href.
        // This is necessary to avoid router.push prepending the base URL to the requested redirect URL.
        if (redirectUrlAfterLogin.includes("/oidc")) {
          window.location.href = redirectUrlAfterLogin;
          return;
        }
        router.push(redirectUrlAfterLogin, routeParams);
      } else if (["/", "/login"].includes(router.pathname)) {
        router.push(
          getDefaultAppRoute({
            setting:
              sessionData.society?.societySettingsPublic?.defaultAppRoute,
          }) ?? FALLBACK_DEFAULT_APP_ROUTE,
          routeParams,
        );
      }
    }
    setSessionLoading(false);
  };

  // Normal login
  const loginCallback = async (
    loginDetails: Required<SocietyAuthBodyInput>,
  ) => {
    const response = await apiRequest({
      contract: postSocietyAuthContract,
      inputs: {
        params: { societyId: getSocietyId() },
        body: loginDetails,
      },
    });
    clearAppCache();
    setSession(response);

    if (!router.pathname.includes("/login")) {
      return;
    }

    await redirectUrlAfterLoginCallback(
      response,
      router.state?.redirectUrlAfterLogin as string | undefined,
    );
  };

  // Token-based login/impersonation entrypoint
  const tokenCallback = async (tokenValue: string) => {
    let response: SpecialResponse | null = null;

    try {
      response = await apiRequest({
        contract: getSocietyTokenValueContract,
        inputs: {
          params: {
            societyId: getSocietyId()!,
            tokenValue,
          },
        },
      });
    } catch (error) {
      console.log("Error:", error);
      response = null;
    }

    if (!lodashIsNill(response) && "usageLogin" in response) {
      // Redirect via the usageLogin info
      const { routePath, pathParam } = response.usageLogin!;
      const url = Object.entries(pathParam as object).reduce(
        (u, [key, val]) => u?.replace(`:${key}`, val.toString()),
        routePath,
      );
      router.removeFromState("token");
      router.removeFromState("impersonate");
      await refreshSessionCallback(url, {
        ...router.state,
        token: null,
        impersonate: null,
      });
    } else {
      // Fallback to normal refresh
      await refreshSessionCallback();
    }
  };

  const isAuthenticated = !!session?.societyUserId;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const withAuthentification = useCallback(
    <Callback extends AsyncFunction>(
      callback: Callback,
    ): ((
      ...args: Parameters<Callback>
    ) => ReturnType<Callback> | Promise<void>) => {
      return async (...args) => {
        if (!isAuthenticated) {
          try {
            await loginModalControl.openAsync();
          } catch (error) {
            return;
          }
        }

        return callback(...args);
      };
    },
    [isAuthenticated],
  );

  // Apply theme and auto-logout listener when session changes
  useEffect(() => {
    if (router.state?.logout !== undefined && session?.profileId) {
      setSessionLoading(true);
      logoutCallback();
    }
  }, [session, router.state?.logout]);
  useIFrameCheckSession({
    profileId: session?.profileId,
    redirectUrlAfterLogin: router.state?.redirectUrlAfterLogin as
      | string
      | undefined,
    sessionIsLoading:
      sessionLoading || session === undefined || session === null,
  });

  const value = useMemo(
    () => ({
      setSession,
      logout: logoutCallback,
      login: loginCallback,
      token: tokenCallback,
      refreshSession: refreshSessionCallback,
      withAuthentification,
      isPhoneMedia,
      environment: session?.environment ?? "prod",
      societyUser: session?.societyUser,
      society: session?.society,
      profile: session?.profile,
      societyAdmin: session?.societyAdmin,
      iat: session?.iat ?? 0,
      societyUserId: session?.societyUserId,
      societyAdminId: session?.societyAdminId,
      adminMode: session?.adminMode ?? false,
      profileId: session?.profileId,
      societyId: session?.societyId,
      societies: session?.societies ?? [],
      profileIds: session?.profileIds ?? [],
      tags: session?.tags ?? [],
    }),
    [
      session,
      logoutCallback,
      loginCallback,
      tokenCallback,
      refreshSessionCallback,
    ],
  );

  useEffect(() => {
    // Calling onClose() to drop the pending promise
    loginModalControl.onClose();
  }, [router.pathname]);
  if (
    !session?.societyId ||
    (sessionLoading && !session.profileId) ||
    !session.society
  ) {
    return <LoadingPage />;
  }

  if (value.profile) {
    return (
      <CustomSessionContext.Provider value={value}>
        <HubVisibleViewPortProvider>
          <SocietySessionContext.Provider value={session.society}>
            <ThemeProvider theme={session?.society?.theme}>
              <ProfileContext.Provider
                value={{
                  ...value.profile,
                  refreshProfile: async () => value.refreshSession(),
                  isLoading: false,
                  isFetching: false,
                }}
              >
                <FirebaseAnalyticsProvider />
                <NotificationsProvider>
                  {children}
                  <LoginModal control={loginModalControl} />
                </NotificationsProvider>
              </ProfileContext.Provider>
            </ThemeProvider>
          </SocietySessionContext.Provider>
        </HubVisibleViewPortProvider>
      </CustomSessionContext.Provider>
    );
  }
  return (
    <CustomSessionContext.Provider value={value}>
      <HubVisibleViewPortProvider>
        <SocietySessionContext.Provider value={session.society}>
          <ThemeProvider theme={session?.society?.theme}>
            <ProfileContext.Provider
              value={{
                ...((value.profile ?? {}) as ProfileView),
                refreshProfile: async () => value.refreshSession(),
                isLoading: false,
                isFetching: false,
              }}
            >
              <FirebaseAnalyticsProvider />
              <NotificationsProvider>
                {children}
                <LoginModal control={loginModalControl} />
              </NotificationsProvider>
            </ProfileContext.Provider>
          </ThemeProvider>
        </SocietySessionContext.Provider>
      </HubVisibleViewPortProvider>
    </CustomSessionContext.Provider>
  );
};
